import { Component, computed, effect, inject, input, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActiveThreadComponent } from 'src/app/features/general-agent/pages/active-thread/active-thread.component';
import { ThreadInputComponent } from 'src/app/features/general-agent/components/thread/thread-input/thread-input.component';
import { PopoverComponent } from 'src/app/@core/components/ui/popover/popover.component';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { MenuComponent, CustomMenuItem } from 'src/app/@core/components/ui/menu/menu.component';
import { IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { ToastService } from 'src/app/@core/services/toast.service';
import { THREAD_AGENT_SERVICE } from '../../tokens/thread-agent.token';
import { AGENT_MODE, ChatMessagePayload, QueryOptions } from 'src/app/features/general-agent/models/types';
import { LLM_PROVIDERS } from 'src/app/features/general-agent/models/model';
import { SearchBarComponent } from 'src/app/@shared/components/search-bar/search-bar.component';
import { TruncatePipe } from 'src/app/@shared/pipes/truncate.pipe';
import { EditThreadComponent } from 'src/app/features/general-agent/components/dialogs/edit-thread/edit-thread.component';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { v4 as uuidv4 } from 'uuid';


@Component({
    selector: 'app-chat-tab-shell',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ActiveThreadComponent,
        ThreadInputComponent,
        PopoverComponent,
        ButtonComponent,
        MenuComponent,
        SearchBarComponent,
        TruncatePipe,
        EditThreadComponent,
        CreditsOverviewComponent,
    ],
    templateUrl: './chat-tab-shell.component.html',
})
export class ChatTabShellComponent {
    contractId = input.required<string>();
    comparisonId = input<string>('');

    /** contractId for doc-chat, comparisonId for compare-chat. */
    private primaryId = computed(() => this.comparisonId() || this.contractId());
    documentTypeId = input<string>('');
    projectId = input<string>('');
    compareDocuments = input<{ _id: string; name: string }[]>([]);
    kpiDocuments = input<{ _id: string; name: string }[]>([]);
    formQuestions = input<{ _id: string; name: string; sectionTitle?: string }[]>([]);
    selectedFormQuestionId = input<string | null>(null);
    embeddingsStatus = input<boolean>(true);

    @ViewChild('historyPanel') historyPanel!: PopoverComponent;
    @ViewChild('threadOptionsMenu') threadOptionsMenu!: MenuComponent;

    currentThreadForMenu = signal<any>(null);

    private toastService = inject(ToastService);
    private chatTabService = inject(THREAD_AGENT_SERVICE);

    selectedThreadId = signal<string>('');
    selectedThreadMetadataId = signal<string>('');
    isSending = signal<boolean>(false);

    threads = signal<any[]>([]);
    threadsLoading = signal<boolean>(false);
    searchText = signal<string>('');

    editThreadDialogVisible = false;
    threadTitleForEdit = '';
    threadToRename: { _id: string; title?: string } | null = null;

    selectedProvider = signal<LLM_PROVIDERS>(LLM_PROVIDERS.ANTHROPIC);

    currentMode = signal<AGENT_MODE | null>(null);

    historyPanelOpen = signal<boolean>(false);

    threadsGroupedByDate = signal<any[][]>([[], [], []]);

    constructor() {
        effect(() => {
            const id = this.primaryId();
            if (id) {
                this.loadThreads();
            }
        });
        effect(() => {
            this.threads();
            this.searchText();
            this.buildThreadsGroupedByDate();
        });
    }

    private async loadThreads(): Promise<void> {
        if (!this.primaryId()) return;
        this.threadsLoading.set(true);
        try {
            const res = await this.chatTabService.getThreads(this.primaryId());
            if (res.success !== false) {
                this.threads.set(res.data ?? (res as any) ?? []);
            }
        } catch (err) {
            console.error('[ChatTabShell] Failed to load threads', err);
        } finally {
            this.threadsLoading.set(false);
        }
    }

    private buildThreadsGroupedByDate(): void {
        const list = this.threads();
        const q = (this.searchText() || '').toLowerCase().trim();
        const filtered = q
            ? list.filter((t) => (t.title || 'Untitled chat').toLowerCase().includes(q))
            : list;
        const now = new Date();
        const today: any[] = [];
        const last7Days: any[] = [];
        const last30Days: any[] = [];
        for (const t of filtered) {
            const createdAt = t.createdAt ? new Date(t.createdAt) : new Date(0);
            const diffInDays = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
            if (diffInDays === 0) today.push(t);
            else if (diffInDays <= 7) last7Days.push(t);
            else if (diffInDays <= 30) last30Days.push(t);
        }
        this.threadsGroupedByDate.set([today, last7Days, last30Days]);
    }

    hasAnyThreads(): boolean {
        return this.threadsGroupedByDate().some((g) => g.length > 0);
    }

    toggleHistoryPanel(event: Event): void {
        if (!this.historyPanelOpen()) {
            this.loadThreads();
        }
        this.historyPanel.toggle(event);
    }

    selectThread(thread: any): void {
        this.selectedThreadId.set('');
        this.selectedThreadMetadataId.set('');
        this.currentMode.set(null);
        setTimeout(() => {
            this.selectedThreadId.set(thread._id);
            this.selectedThreadMetadataId.set(thread.threadMetadataId ?? '');
        });
        this.historyPanel.hide();
    }

    searchThreads(value: string | Event): void {
        const text = typeof value === 'string' ? value : (value?.target as HTMLInputElement)?.value ?? '';
        this.searchText.set(text || '');
        this.buildThreadsGroupedByDate();
    }

    getThreadMenuItems(thread: any): CustomMenuItem[] {
        if (!thread) return [];
        return [
            {
                label: 'Rename',
                icon: 'pencil',
                iconSource: IconSourceEnum.LUCIDE,
                callback: () => {
                    this.openRenameDialog(thread);
                    this.threadOptionsMenu?.hide();
                },
            },
            {
                label: 'Delete',
                icon: 'trash-2',
                iconSource: IconSourceEnum.LUCIDE,
                class: 'text-danger',
                callback: () => {
                    this.deleteThread(thread);
                    this.threadOptionsMenu?.hide();
                },
            },
        ];
    }

    openThreadMenu(event: Event, thread: any): void {
        this.currentThreadForMenu.set(thread);
        this.threadOptionsMenu.toggle(event);
    }

    openRenameDialog(thread: any): void {
        this.threadToRename = thread;
        this.threadTitleForEdit = thread.title || 'Untitled chat';
        this.editThreadDialogVisible = true;
    }

    onSaveThreadTitle(): void {
        if (!this.threadToRename || !this.threadTitleForEdit.trim()) return;
        const newTitle = this.threadTitleForEdit.trim();
        this.chatTabService.updateThreadTitle(this.threadToRename._id, newTitle).then(() => {
            this.toastService.success('Thread name updated successfully');
            this.threads.update((list) =>
                list.map((t) => (t._id === this.threadToRename!._id ? { ...t, title: newTitle } : t))
            );
            this.buildThreadsGroupedByDate();
            this.editThreadDialogVisible = false;
            this.threadToRename = null;
        }).catch(() => {
            this.toastService.error('Failed to update thread name');
        });
    }

    deleteThread(thread: any): void {
        this.chatTabService.deleteThread(thread._id).then(() => {
            this.toastService.success('Thread deleted successfully');
            if (this.selectedThreadId() === thread._id) {
                this.startNewChat();
            }
            this.threads.update((list) => list.filter((t) => t._id !== thread._id));
            this.buildThreadsGroupedByDate();
        }).catch(() => {
            this.toastService.error('Failed to delete conversation');
        });
    }

    startNewChat(): void {
        this.selectedThreadId.set('');
        this.selectedThreadMetadataId.set('');
        this.currentMode.set(null);
        this.historyPanel.hide();
    }

    async sendFirstMessage(payload: ChatMessagePayload): Promise<void> {
        if (this.isSending()) return;
        this.isSending.set(true);
        if (payload.provider) {
            this.selectedProvider.set(payload.provider);
        }
        if (payload.mode) {
            this.currentMode.set(payload.mode);
        }
        try {
            const messageId = uuidv4();

            // If compare documents exist but none were explicitly selected, fall back to all of them.
            let tools = payload.tools ?? [];
            const allCompareDocs = this.compareDocuments();
            if (allCompareDocs.length > 0) {
                const hasSelection = tools.some(
                    t => t && QueryOptions.COMPARE_DOCS in t && (t[QueryOptions.COMPARE_DOCS] as string[]).length > 0
                );
                if (!hasSelection) {
                    tools = [
                        ...tools.filter(t => !(t && QueryOptions.COMPARE_DOCS in t)),
                        { [QueryOptions.COMPARE_DOCS]: allCompareDocs.map(d => d._id) },
                    ];
                }
            }

            const res = await this.chatTabService.runThread({
                ...payload,
                tools,
                threadId: '',
                threadMetadataId: '',
                documentId: this.contractId(),
                messageId,
                ...(this.comparisonId() ? { comparisonId: this.comparisonId() } : {}),
            });
            if (res?.success && res?.data) {
                const threadId = res.data.thread?._id ?? res.data.threadId;
                const threadMetadataId =
                    res.data.threadMetadata?.slice(-1)[0]?._id ?? res.data.threadMetadataId;
                if (threadId && threadMetadataId) {
                    this.selectedThreadId.set(threadId);
                    this.selectedThreadMetadataId.set(threadMetadataId);
                }
            } else {
                this.toastService.error("Failed to start chat");
            }
        } catch (err) {
            console.error('[ChatTabShell] sendFirstMessage error', err);
            this.toastService.error("Failed to start chat");
        } finally {
            this.isSending.set(false);
        }
    }

    onThreadDeleted(): void {
        const deletedId = this.selectedThreadId();
        this.threads.update(list => list.filter(t => t._id !== deletedId));
        this.selectedThreadId.set('');
        this.selectedThreadMetadataId.set('');
    }
}
